import type { ErrorInfo, ReactNode } from 'react';

import { sentryCaptureException } from '@/utils/sentry';
import { ErrorBoundary } from 'react-error-boundary';

import { AppErrorFallback } from './fallback';

interface AppErrorBoundaryProps {
  children: ReactNode;
  /**
   * Custom fallback to render when an error is caught.
   * Defaults to `AppErrorFallback` which shows an inline error message
   * with a retry button and optional error details toggle.
   */
  fallback?: ReactNode;
  /**
   * When any value in this array changes, the error boundary resets
   * and attempts to re-render its children. Useful for resetting
   * on route changes (e.g. pass `[location.pathname]`).
   */
  resetKeys?: unknown[];
}

function handleError(error: Error, info: ErrorInfo) {
  sentryCaptureException(error, {
    extra: { componentStack: info.componentStack },
  });
}

/**
 * A generic error boundary that reports errors to Sentry and renders
 * a sensible fallback UI. Drop this around any feature or section to
 * prevent it from crashing the entire page.
 *
 * @example
 * // With default fallback (inline error message + retry + error details)
 * <AppErrorBoundary>
 *   <SomeFeature />
 * </AppErrorBoundary>
 *
 * @example
 * // With custom fallback
 * <AppErrorBoundary fallback={<Text>Something went wrong</Text>}>
 *   <SomeFeature />
 * </AppErrorBoundary>
 *
 * @example
 * // Silent fallback (swallow errors, still reports to Sentry)
 * <AppErrorBoundary fallback={null}>
 *   <ExperimentalFeature />
 * </AppErrorBoundary>
 *
 * @example
 * // Reset on route change
 * const location = useLocation();
 * <AppErrorBoundary resetKeys={[location.pathname]}>
 *   <Outlet />
 * </AppErrorBoundary>
 */
export const AppErrorBoundary = ({ children, fallback, resetKeys }: AppErrorBoundaryProps) => {
  if (fallback !== undefined) {
    return (
      <ErrorBoundary fallback={fallback ?? <></>} onError={handleError} resetKeys={resetKeys}>
        {children}
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary FallbackComponent={AppErrorFallback} onError={handleError} resetKeys={resetKeys}>
      {children}
    </ErrorBoundary>
  );
};
